import {
  createApiClient,
  type AdminLoginResult,
  type AdminSession,
  type HealthResponse,
  type LoginInput,
  type TwoFactorEnableResponse,
  type TwoFactorSetupResponse,
} from '@duvamarine/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasSessionHint, useAdminAuthStore } from './auth-store';
import { currentLocale } from './i18n';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

export const api = createApiClient<AdminSession>({
  baseUrl: BASE_URL,
  refreshPath: '/admin/auth/refresh',
  getAccessToken: () => useAdminAuthStore.getState().accessToken,
  onSession: (session) => useAdminAuthStore.getState().setSession(session),
  onSessionLost: () => useAdminAuthStore.getState().clear(),
  getLocale: currentLocale,
});

export async function bootstrapSession(): Promise<void> {
  if (!hasSessionHint()) {
    useAdminAuthStore.getState().clear();
    return;
  }
  const session = await api.refresh();
  if (!session) useAdminAuthStore.getState().clear();
}

export function useAdminLogin() {
  return useMutation({
    mutationFn: (input: LoginInput) =>
      api.post<AdminLoginResult>('/admin/auth/login', input, { auth: false }),
    onSuccess: (result, input) =>
      useAdminAuthStore.getState().setChallenge({
        token: result.challengeToken,
        step: result.status === 'TWO_FACTOR_SETUP_REQUIRED' ? 'setup' : 'verify',
        expiresAt: Date.now() + result.expiresIn * 1000,
        email: input.email,
      }),
  });
}

export const useTwoFactorSetup = () =>
  useMutation({
    mutationFn: (challengeToken: string) =>
      api.post<TwoFactorSetupResponse>(
        '/admin/auth/2fa/setup',
        { challengeToken },
        { auth: false },
      ),
  });

/** Session is applied by the caller only after the recovery codes have been acknowledged. */
export const useTwoFactorEnable = () =>
  useMutation({
    mutationFn: (input: { challengeToken: string; code: string }) =>
      api.post<TwoFactorEnableResponse>('/admin/auth/2fa/enable', input, { auth: false }),
  });

export const useTwoFactorVerify = () =>
  useMutation({
    mutationFn: (input: { challengeToken: string; code?: string; recoveryCode?: string }) =>
      api.post<AdminSession>('/admin/auth/2fa/verify', input, { auth: false }),
    onSuccess: (session) => useAdminAuthStore.getState().setSession(session),
  });

export function useAdminLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/admin/auth/logout'),
    onSettled: () => {
      useAdminAuthStore.getState().clear();
      queryClient.clear();
    },
  });
}

/** Health returns 503 with a body when degraded, so it is read without the error-throwing client. */
export const useHealth = () =>
  useQuery({
    queryKey: ['health'],
    queryFn: async (): Promise<HealthResponse | null> => {
      try {
        const response = await fetch(`${BASE_URL}/health`, {
          headers: { Accept: 'application/json' },
        });
        return (await response.json()) as HealthResponse;
      } catch {
        return null;
      }
    },
    refetchInterval: 30_000,
  });
